import { ButtonElement, DreamBlock, ImageElement } from '@typedream/core';
import {
  findNodePath,
  focusEditor,
  getAboveNode,
  getEditorString,
  getLastNodeByLevel,
  getNode,
  getNodeParent,
  isBlock,
  isElement,
  setNodes,
  unsetNodes,
  unwrapNodes,
} from '@udecode/plate-core';
import { merge } from 'lodash';
import { Path, Range } from 'slate';
import { LINK_TYPES } from '../constants';
import { BLOCK_ELEMENT_KEY } from '../nodes/collection/components/CollectionDataElement/types';
import {
  DreamElementKey,
  ElementPluginOptions,
  getTElementPlugin,
  getTPlugin,
} from '../plate';
import {
  ELEMENT_BLOCKQUOTE,
  ELEMENT_BUTTON,
  ELEMENT_CONTAINER,
  ELEMENT_FORM_INPUT,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_LI,
  ELEMENT_LINK,
  ELEMENT_PARAGRAPH,
  ELEMENT_TODO_LI,
  KEY_SINGLE_CHILD,
} from '../pluginKeys';
import { getSelectedElementEntry } from '../plugins/elementSelection/queries';
import { SingleChildPlugin } from '../plugins/singleChild/types';
import { DreamEditor } from '../types';

export function getBlockPlugin(
  editor: DreamEditor,
  options?: { type?: DreamBlock['type']; at?: Path },
) {
  let type = options?.type;

  if (!type) {
    const checkedPath = options?.at || editor.store.getState().selectedElementPaths[0];
    if (!checkedPath) return null;

    const element = getNode<DreamBlock>(editor, checkedPath);
    if (!element) return null;

    type = element.type;
  }

  return getTElementPlugin(editor, type);
}

export function getBlockFormat(
  editor: DreamEditor,
  options?: { type?: DreamBlock['type']; at?: Path },
) {
  const plugin = getBlockPlugin(editor, options);
  return (plugin?.options as ElementPluginOptions)?.format;
}

export function getBlockMetadata(
  editor: DreamEditor,
  options?: { type?: DreamBlock['type']; at?: Path },
) {
  const plugin = getBlockPlugin(editor, options);
  return (plugin?.options as ElementPluginOptions)?.meta;
}

export function getElementEntry(editor: DreamEditor) {
  if (!editor.selection) return null;

  return getAboveNode<DreamBlock>(editor, {
    at: editor.selection.focus.path,
    match: (node) => isElement(node),
  });
}

export function getElement(editor: DreamEditor) {
  return getElementEntry(editor)?.[0];
}

export function isBlockEmpty(editor: DreamEditor, element: any) {
  const children = (element?.children as Array<any>) || [];
  if (element && !element.type) {
    return element.text === '';
  }
  return (
    children
    && (children.length <= 0 || (children.length === 1 && children[0].text === ''))
  );
}

export function isTextBlock(editor: DreamEditor, element: DreamBlock): boolean {
  // a text block contains text nodes as direct child
  return (
    isBlock(editor, element)
    && [
      ELEMENT_PARAGRAPH,
      ELEMENT_H1,
      ELEMENT_H2,
      ELEMENT_H3,
      ELEMENT_LI,
      ELEMENT_TODO_LI,
      ELEMENT_BLOCKQUOTE,
      ELEMENT_BUTTON,
    ].includes(element.type)
  );
}

export function isInputBlock(editor: DreamEditor, element: DreamBlock): boolean {
  // an input block contains input nodes as direct child
  return (
    isBlock(editor, element)
    && [ELEMENT_FORM_INPUT].includes(element.type)
  );
}

export function hasProperty<K extends DreamElementKey>(
  editor: DreamEditor,
  element: DreamBlock,
  property: K,
): element is Extract<DreamBlock, { [T in K]?: any }> {
  if (!isElement(element)) return false;

  const plugin = getTElementPlugin(editor, element.type);

  // if plugin.options.format.validProperties is a function, call it with the element
  if (typeof plugin.options?.format?.validProperties === 'function') {
    return plugin.options?.format?.validProperties(element).includes(property);
  }

  return !!plugin.options?.format?.validProperties?.includes(property);
}

export function isPathRoot(path: Path) {
  return path.length <= 2;
}

export function isLinkableElement(
  element: DreamBlock,
): element is Extract<
DreamBlock,
{ type: 'a' | 'img' | 'button' | 'container' }
> {
  const linkableElements: DreamBlock['type'][] = [
    'a',
    'button',
    'img',
    'container',
  ];
  return linkableElements.includes(element.type);
}

// TODO: handle internal link
export function getURL(element: DreamBlock) {
  return element.type === 'img'
    ? element.href
    : (element as Extract<DreamBlock, { url?: string }>).url;
}

// TODO: handle internal link
export function getEditorURL(element: DreamBlock) {
  return getURL(element);
}

export function getBlockOptions(editor: DreamEditor, element: DreamBlock) {
  const plugin = getTElementPlugin(editor, element.type);
  const path = findNodePath(editor, element);

  let { options } = plugin;

  if (path) {
    try {
      const parent = getNodeParent<DreamBlock>(editor, path);
      if (isElement(parent)) {
        const parentPlugin = getTElementPlugin(editor, parent.type);
        const overrideChildOptions = typeof parentPlugin.options?.overrideChildOptions === 'function'
          ? parentPlugin.options?.overrideChildOptions(element)
          : parentPlugin.options?.overrideChildOptions;
        options = merge({}, plugin.options, overrideChildOptions);
      }
    } catch (error) {
      /*  */
    }
  }

  return options;
}

export const getAddNewBlockPath = (editor: DreamEditor) => {
  const selectedElementEntry = getSelectedElementEntry(editor);
  const lastNodeEntry = getLastNodeByLevel(editor, 1);

  let insertPath: Path;

  if (selectedElementEntry) {
    if (
      selectedElementEntry[0].type === ELEMENT_CONTAINER
      && isBlockEmpty(editor, selectedElementEntry[0])
    ) {
      insertPath = [...selectedElementEntry[1], 0];
    } else {
      insertPath = Path.next(selectedElementEntry[1]);
    }
  } else if (lastNodeEntry) {
    insertPath = Path.next(lastNodeEntry[1]);
  } else {
    insertPath = [0, 0];
  }

  return insertPath;
};

export const canInsertBlock = (editor: DreamEditor, path: Path) => {
  const singleChildPlugin = getTPlugin<SingleChildPlugin>(
    editor,
    KEY_SINGLE_CHILD,
  );

  const rules = singleChildPlugin.options?.rules || [];

  if (rules.some((rule) => Path.isChild(path as Path, rule.path))) {
    return false;
  }

  return true;
};

export const canRemoveBlock = (editor: DreamEditor, path: Path) => {
  const singleRuleAllowed = canInsertBlock(editor, path);
  const selectedElementEntry = getSelectedElementEntry(editor);
  if (!selectedElementEntry) return singleRuleAllowed;

  const [node] = selectedElementEntry;
  const opt = getTPlugin(editor, node.type).options as ElementPluginOptions;

  if (opt?.isDeletable) {
    return singleRuleAllowed && opt.isDeletable(editor, { at: path });
  }
  return singleRuleAllowed;
};

export const removeLink = (editor: DreamEditor, selection: Range) => {
  if (selection) {
    const el = getNodeParent(editor, selection.focus.path);
    if (LINK_TYPES.includes(el.type as string)) {
      if (el.type === ELEMENT_LINK) {
        unwrapNodes(editor, {
          match: { type: ELEMENT_LINK },
          at: selection,
        });
      } else {
        unsetNodes<ImageElement>(editor, [
          BLOCK_ELEMENT_KEY.HREF,
          BLOCK_ELEMENT_KEY.INTERNAL_LINK,
          BLOCK_ELEMENT_KEY.OPEN_IN_NEW_TAB,
        ]);
      }
      // This will make editor back in focus and select the link
      // to allow undo and updating other properties
      focusEditor(editor);
    }
  }
};

export const getText = (editor: DreamEditor, options?: { fullBlockText?: boolean }) => {
  if (editor.selection) {
    const selection = options?.fullBlockText
      ? {
        anchor: { ...editor.selection.anchor, offset: 0 },
        focus: editor.selection.focus,
      }
      : editor.selection;
    return getEditorString(editor, selection);
  }
  return '';
};

export const changeLinkOpenInNewTab = (
  editor: DreamEditor,
) => {
  const elementEntry = getSelectedElementEntry(editor);
  if (!elementEntry) return;
  const [element, path] = elementEntry;
  if (isLinkableElement(element)) {
    setNodes(
      editor,
      { open_in_new_tab: !element.open_in_new_tab },
      { at: path },
    );
  }
  // This will make editor back in focus and select the link
  // to allow undo and updating other properties
  focusEditor(editor);
};

export function isGlowingButton(editor: DreamEditor, element: DreamBlock): boolean {
  if (element.type !== ELEMENT_BUTTON) return false;
  const elementEntry = getSelectedElementEntry<ButtonElement>(editor);
  if (!elementEntry) return false;
  return elementEntry[0].style === 'glowing_button';
}
